import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { ExternalLink, Trash2, Pencil, Plus, CheckCircle2, Clock, Radar, Phone, CalendarDays } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { MilestoneCard, isMilestone } from '../components/MilestoneCard';
import { StoryPrompt } from '../components/StoryPrompt';
import { useLang } from '../context/LangContext';
import { WorkerNotes } from '../components/WorkerNotes';
import { ConsentPanel } from '../components/ConsentPanel';
import { HelperPanel } from '../components/HelperPanel';
import { api } from '../lib/api';
import { toast } from 'sonner';

const STATUSES = [
  { v: 'not_started', l: 'Not Started', c: 'bg-[#F5F5F4] text-[#78716C]' },
  { v: 'researching', l: 'Researching', c: 'bg-[#E0F2FE] text-[#0284C7]' },
  { v: 'preparing_docs', l: 'Preparing Documents', c: 'bg-[#FEF3C7] text-[#D97706]' },
  { v: 'submitted', l: 'Submitted', c: 'bg-[#EDE9FE] text-[#7C3AED]' },
  { v: 'under_review', l: 'Under Review', c: 'bg-[#DBEAFE] text-[#2563EB]' },
  { v: 'approved', l: 'Approved', c: 'bg-[#DCFCE7] text-[#16A34A]' },
  { v: 'denied', l: 'Denied', c: 'bg-[#FEE2E2] text-[#DC2626]' },
];

export default function Applications() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [a, p] = await Promise.all([api.get('/applications'), api.get('/programs')]);
      setApps(a.data.applications);
      setPrograms(p.data.programs);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    const prev = apps.find((a) => a.program_id === form.program_id)?.status;
    const { data } = await api.post('/applications', form);
    setApps((cur) => {
      const idx = cur.findIndex((a) => a.program_id === data.program_id);
      if (idx >= 0) { const c = [...cur]; c[idx] = data; return c; }
      return [...cur, data];
    });
    toast.success('Saved');
    setEditing(null);
    if (isMilestone(prev, data.status)) setMilestone(data);
  };

  const del = async (a) => {
    await api.delete(`/applications/${a.app_id}`);
    setApps((cur) => cur.filter((x) => x.app_id !== a.app_id));
    toast.success('Removed');
  };

  const grouped = STATUSES.map((s) => ({ ...s, items: apps.filter((a) => a.status === s.v) }));
  const nextActions = apps.filter((a) => a.next_action).slice(0, 3);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{fontFamily: 'Lexend'}}>{t('My Applications')}</h1>
          <p className="text-muted-foreground mt-2">{t('Track every application, reference number, and next step.')}</p>
        </div>
        <Button onClick={() => setEditing({ program_id: '', status: 'not_started' })} data-testid="add-application-btn" className="bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="w-4 h-4 mr-2" /> {t('Add application')}</Button>
      </div>

      {!loading && apps.length === 0 && (
        <Card className="p-10 text-center bg-card">
          <div className="text-lg font-semibold mb-2" style={{fontFamily: 'Lexend'}}>No applications yet</div>
          <p className="text-muted-foreground mb-4">Start with a benefits screening and add the ones you want to pursue.</p>
          <Button onClick={() => navigate('/find-benefits')} className="bg-primary text-primary-foreground" data-testid="empty-cta">Find my benefits</Button>
        </Card>
      )}

      <WorkerNotes />
      <ConsentPanel />
      <HelperPanel />

      {nextActions.length > 0 && (
        <Card className="p-5 mb-6 bg-primary/5 border-primary/20">
          <div className="text-sm font-semibold flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-primary" /> Your next actions</div>
          <ul className="space-y-2">
            {nextActions.map((a) => (
              <li key={a.app_id} className="text-sm flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span><span className="font-medium">{a.program_name}:</span> {a.next_action}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <StoryPrompt apps={apps} />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {apps.map((a) => {
          const st = STATUSES.find((s) => s.v === a.status);
          const prog = programs.find((p) => p.id === a.program_id);
          return (
            <Card key={a.app_id} className="p-5 bg-card" data-testid={`app-card-${a.program_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <Badge className={`${st?.c} border-0`}>{t(st?.l)}</Badge>
                  <h3 className="mt-2 font-semibold leading-tight" style={{fontFamily: 'Lexend'}}>{a.program_name}</h3>
                  <div className="text-xs text-muted-foreground mt-1">{a.program_category}</div>
                </div>
              </div>
              {a.reference_number && <div className="text-sm mb-1"><span className="text-muted-foreground">Ref #:</span> <span className="font-mono">{a.reference_number}</span></div>}
              {a.submitted_date && <div className="text-sm mb-1"><span className="text-muted-foreground">Submitted:</span> {a.submitted_date}</div>}
              {a.next_action && <div className="text-sm mb-1"><span className="text-muted-foreground">Next:</span> {a.next_action}</div>}
              {a.target_date && !['submitted','under_review','approved','denied'].includes(a.status) && (() => { const dl = Math.ceil((new Date(a.target_date) - new Date()) / 86400000); return <Badge className={`border mb-1 ${dl < 0 ? 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]' : dl <= 7 ? 'bg-[#FFF4E5] text-[#663C00] border-[#FFE0B2]' : 'bg-secondary text-foreground border-border'}`} data-testid={`target-badge-${a.program_id}`}><CalendarDays className="w-3 h-3 mr-1" /> {t('Target date')}: {a.target_date}{dl >= 0 ? ` · ${dl}d` : ` · ${Math.abs(dl)}d late`}</Badge>; })()}
              {a.notes && <p className="text-sm text-foreground/80 mt-2">{a.notes}</p>}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setEditing(a)} data-testid={`edit-${a.program_id}`}><Pencil className="w-4 h-4 mr-1" /> {t('Edit')}</Button>
                <Button size="sm" variant="outline" onClick={() => del(a)} data-testid={`delete-${a.program_id}`}><Trash2 className="w-4 h-4 mr-1" /> {t('Remove')}</Button>
                {prog && <Button asChild size="sm" variant="ghost" data-testid={`gov-${a.program_id}`}><a href={prog.application_url} target="_blank" rel="noopener noreferrer">Gov site <ExternalLink className="w-3 h-3 ml-1" /></a></Button>}
              </div>
              {a.status_check && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button size="sm" className="mt-2 w-full bg-primary/10 text-primary hover:bg-primary/20 border-0" data-testid={`status-check-${a.program_id}`}><Radar className="w-4 h-4 mr-1.5" /> {t('Check official status')}</Button>
                  </PopoverTrigger>
                  <PopoverContent className="bg-card w-80" data-testid={`status-check-popover-${a.program_id}`}>
                    <div className="font-semibold text-sm" style={{fontFamily: 'Lexend'}}>{a.status_check.portal}</div>
                    <p className="text-xs text-muted-foreground mt-1.5">{a.status_check.tip}</p>
                    {a.reference_number && <p className="text-xs mt-2">Have your reference # ready: <span className="font-mono font-semibold">{a.reference_number}</span></p>}
                    <div className="mt-3 flex gap-2">
                      <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 flex-1" data-testid={`status-portal-link-${a.program_id}`}><a href={a.status_check.url} target="_blank" rel="noopener noreferrer">Open portal <ExternalLink className="w-3 h-3 ml-1" /></a></Button>
                      <Button asChild size="sm" variant="outline"><a href={`tel:${a.status_check.phone.replace(/[^0-9+]/g, '')}`}><Phone className="w-3 h-3 mr-1" /> {a.status_check.phone}</a></Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </Card>
          );
        })}
      </div>

      <div className="mt-10 grid gap-4">
        <h2 className="text-xl font-bold" style={{fontFamily: 'Lexend'}}>{t('Pipeline view')}</h2>
        <div className="grid md:grid-cols-4 xl:grid-cols-7 gap-3">
          {grouped.map((g) => (
            <div key={g.v} className={`p-3 rounded-xl border ${g.c} min-h-[100px]`}>
              <div className="text-xs font-semibold mb-2">{g.l} ({g.items.length})</div>
              <ul className="space-y-1">
                {g.items.map((a) => <li key={a.app_id} className="text-xs bg-white/60 rounded p-2">{a.program_name}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <EditDialog editing={editing} setEditing={setEditing} save={save} programs={programs} />
      )}
      {milestone && <MilestoneCard app={milestone} onClose={() => setMilestone(null)} />}
    </div>
  );
}

function EditDialog({ editing, setEditing, save, programs }) {
  const [form, setForm] = useState({
    program_id: editing.program_id || '',
    status: editing.status || 'not_started',
    reference_number: editing.reference_number || '',
    submitted_date: editing.submitted_date || '',
    notes: editing.notes || '',
    next_action: editing.next_action || '',
    target_date: editing.target_date || '',
  });
  return (
    <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
      <DialogContent className="bg-card">
        <DialogHeader><DialogTitle>Application details</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Program</label>
            <Select value={form.program_id} onValueChange={(v) => setForm({ ...form, program_id: v })}>
              <SelectTrigger data-testid="edit-program-select" className="h-11"><SelectValue placeholder="Choose a program" /></SelectTrigger>
              <SelectContent className="bg-card">
                {programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Status</label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="edit-status-select" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card">
                {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Reference #</label>
              <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} data-testid="edit-ref" className="h-11" />
            </div>
            <div>
              <label className="text-sm font-medium">Submitted date</label>
              <Input type="date" value={form.submitted_date} onChange={(e) => setForm({ ...form, submitted_date: e.target.value })} data-testid="edit-date" className="h-11" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Target date (we'll email you 7 days before)</label>
            <Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} data-testid="edit-target-date" className="h-11" />
          </div>
          <div>
            <label className="text-sm font-medium">Next action</label>
            <Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} placeholder="e.g. Get doctor signature on T2201" data-testid="edit-next" className="h-11" />
          </div>
          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditing(null)} data-testid="edit-cancel">Cancel</Button>
          <Button onClick={() => save(form)} disabled={!form.program_id} data-testid="edit-save" className="bg-primary text-primary-foreground">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
